import { useEffect, useRef } from 'react';
import { useMediaQuery } from '@mantine/hooks';
import type { StoreConfig } from '../services/types';

/**
 * React hook that dynamically positions a specified item above the superbar (#caddie-root) on mobile devices.
 *
 * The hook uses optimized MutationObservers to watch for DOM changes and automatically sets up positioning
 * when the target elements become available. It calculates the required bottom offset for the item
 * so it always appears directly above the superbar, accounting for the superbar's height and any bottom offset.
 *
 * Usage:
 *   usePositionItemOnTopOfSuperbar({
 *     isAvailableToCurrentUser,
 *     storeConfig,
 *   });
 *
 * @param isAvailableToCurrentUser - Whether the current user should see the positioned item.
 * @param storeConfig - The store configuration object, which may include htmlSelectors.onTopOfSuperbar to specify the selector for the item to position.
 */
export function usePositionItemOnTopOfSuperbar({
  isAvailableToCurrentUser,
  storeConfig,
}: {
  isAvailableToCurrentUser: boolean;
  storeConfig?: StoreConfig;
}) {
  const isMobile = useMediaQuery('(max-width: 767px)');

  // Store current values in refs so MutationObserver always has access to latest values
  const currentValuesRef = useRef({
    isAvailableToCurrentUser,
    isMobile,
    storeConfig,
  });

  // Update refs on every render to ensure observer has current values
  currentValuesRef.current = {
    isAvailableToCurrentUser,
    isMobile,
    storeConfig,
  };

  const observersRef = useRef<{
    domObserver: MutationObserver | null;
    resizeObserver: ResizeObserver | null;
    mutationObserver: MutationObserver | null;
    isPositioning: boolean;
    throttleTimeoutId: NodeJS.Timeout | null;
  }>({
    domObserver: null,
    resizeObserver: null,
    mutationObserver: null,
    isPositioning: false,
    throttleTimeoutId: null,
  });

  const originalBottomRef = useRef<{
    el: HTMLElement | null;
    value: string | null;
    selector: string | null;
  }>({ el: null, value: null, selector: null });

  useEffect(() => {
    const cleanup = () => {
      // Clear throttle timeout
      if (observersRef.current.throttleTimeoutId) {
        clearTimeout(observersRef.current.throttleTimeoutId);
        observersRef.current.throttleTimeoutId = null;
      }

      // Disconnect all observers
      if (observersRef.current.domObserver) {
        observersRef.current.domObserver.disconnect();
        observersRef.current.domObserver = null;
      }
      if (observersRef.current.resizeObserver) {
        observersRef.current.resizeObserver.disconnect();
        observersRef.current.resizeObserver = null;
      }
      if (observersRef.current.mutationObserver) {
        observersRef.current.mutationObserver.disconnect();
        observersRef.current.mutationObserver = null;
      }
      observersRef.current.isPositioning = false;
    };

    const restoreOriginalPosition = (item: HTMLElement, selector: string) => {
      try {
        if (
          originalBottomRef.current.el === item &&
          originalBottomRef.current.selector === selector &&
          originalBottomRef.current.value !== null
        ) {
          item.style.bottom = originalBottomRef.current.value;
          // Clear the reference since we've restored it
          originalBottomRef.current = { el: null, value: null, selector: null };
        }
      } catch (error) {
        console.warn('Error restoring original position:', error);
      }
    };

    const setupPositioning = (
      superbar: HTMLElement,
      item: HTMLElement,
      selector: string
    ) => {
      try {
        // Save the original bottom only if this is a new element or selector
        if (
          originalBottomRef.current.el !== item ||
          originalBottomRef.current.selector !== selector
        ) {
          originalBottomRef.current = {
            el: item,
            value: item.style.bottom || '',
            selector,
          };
        }

        const setItemPosition = () => {
          try {
            if (
              !item ||
              !superbar ||
              !document.contains(item) ||
              !document.contains(superbar)
            ) {
              return;
            }

            const superbarRect = superbar.getBoundingClientRect();
            const superbarStyles = window.getComputedStyle(superbar);
            const superbarBottom = parseInt(superbarStyles.bottom) || 0;
            const superbarHeight = superbarRect.height;
            const offset = superbarBottom + superbarHeight;

            item.style.setProperty('bottom', `${offset}px`, 'important');
          } catch (error) {
            console.warn('Error setting item position:', error);
          }
        };

        setItemPosition();

        // Clean up existing observers for this positioning
        if (observersRef.current.resizeObserver) {
          observersRef.current.resizeObserver.disconnect();
        }
        if (observersRef.current.mutationObserver) {
          observersRef.current.mutationObserver.disconnect();
        }

        // Observe changes to the superbar's size
        observersRef.current.resizeObserver = new ResizeObserver(() => {
          setItemPosition();
        });
        observersRef.current.resizeObserver.observe(superbar);

        // Observe changes to the superbar's style or class attributes
        observersRef.current.mutationObserver = new MutationObserver(() => {
          setItemPosition();
        });
        observersRef.current.mutationObserver.observe(superbar, {
          attributes: true,
          attributeFilter: ['style', 'class'],
        });

        observersRef.current.isPositioning = true;
      } catch (error) {
        console.warn('Error setting up positioning:', error);
      }
    };

    const checkAndSetupPositioning = () => {
      try {
        // Always get current values from ref (never stale!)
        const { isAvailableToCurrentUser, isMobile, storeConfig } =
          currentValuesRef.current;
        const itemSelector = storeConfig?.htmlSelectors?.onTopOfSuperbar;

        if (!itemSelector || !isAvailableToCurrentUser || !isMobile) {
          // If conditions aren't met, restore original position if we were positioning
          if (observersRef.current.isPositioning) {
            const item = itemSelector
              ? (document.querySelector(itemSelector) as HTMLElement | null)
              : null;
            if (item && itemSelector) {
              restoreOriginalPosition(item, itemSelector);
            }
            // Clean up positioning observers
            if (observersRef.current.resizeObserver) {
              observersRef.current.resizeObserver.disconnect();
              observersRef.current.resizeObserver = null;
            }
            if (observersRef.current.mutationObserver) {
              observersRef.current.mutationObserver.disconnect();
              observersRef.current.mutationObserver = null;
            }
            observersRef.current.isPositioning = false;
          }
          return;
        }

        const superbar = document.getElementById('caddie-root');
        const item = document.querySelector(itemSelector) as HTMLElement | null;

        // Verify elements are still in DOM
        if (
          superbar &&
          item &&
          document.contains(superbar) &&
          document.contains(item)
        ) {
          if (!observersRef.current.isPositioning) {
            setupPositioning(superbar, item, itemSelector);
          }
        } else {
          // Elements not found or not in DOM, restore if we were positioning
          if (observersRef.current.isPositioning) {
            // Try to find the item to restore it
            const currentItem = itemSelector
              ? (document.querySelector(itemSelector) as HTMLElement | null)
              : null;
            if (currentItem && itemSelector) {
              restoreOriginalPosition(currentItem, itemSelector);
            }
            // Clean up observers
            if (observersRef.current.resizeObserver) {
              observersRef.current.resizeObserver.disconnect();
              observersRef.current.resizeObserver = null;
            }
            if (observersRef.current.mutationObserver) {
              observersRef.current.mutationObserver.disconnect();
              observersRef.current.mutationObserver = null;
            }
            observersRef.current.isPositioning = false;
          }
        }
      } catch (error) {
        console.warn('Error in checkAndSetupPositioning:', error);
      }
    };

    // Throttled version of the positioning check to prevent excessive calls
    const throttledCheck = () => {
      if (observersRef.current.throttleTimeoutId) {
        clearTimeout(observersRef.current.throttleTimeoutId);
      }

      observersRef.current.throttleTimeoutId = setTimeout(() => {
        checkAndSetupPositioning();
      }, 16); // ~60fps throttling
    };

    // Initial check
    checkAndSetupPositioning();

    // Set up DOM observer to watch for changes - optimized to watch specific areas
    try {
      observersRef.current.domObserver = new MutationObserver(throttledCheck);

      // More targeted observation - watch body but with throttling
      observersRef.current.domObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class', 'id'],
      });
    } catch (error) {
      console.warn('Error setting up DOM observer:', error);
    }

    return () => {
      cleanup();
      // Restore original position on cleanup
      try {
        const currentStoreConfig = currentValuesRef.current.storeConfig;
        const itemSelector = currentStoreConfig?.htmlSelectors?.onTopOfSuperbar;
        if (
          itemSelector &&
          originalBottomRef.current.selector === itemSelector
        ) {
          const item = document.querySelector(
            itemSelector
          ) as HTMLElement | null;
          if (item) {
            restoreOriginalPosition(item, itemSelector);
          }
        }
      } catch (error) {
        console.warn('Error during cleanup restoration:', error);
      }
    };
  }, []);
}
